import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { MutationConfig, queryClient, TypedMutationConfig } from '@/lib/react-query';
import { ApiResponse, AxiosErrorCustom, axiosInstance } from '@/lib/axios';
import { GET_TIKTOK_CHANNELS_QUERY_KEY, TGetTiktokChannelsResponse } from './get-tiktok-channels.api';
import { TChannel } from '../../../_shared/types/channel.type';

/**
 * Delete TikTok channel input schema
 */
export const deleteTiktokChannelValidationSchema = z.object({
  organizationChannelId: z.string({ required_error: 'Organization channel id is required' }),
});
export type TDeleteTiktokChannelValidation = z.infer<typeof deleteTiktokChannelValidationSchema>;
export type TDeleteTiktokChannelPayload = TDeleteTiktokChannelValidation;
export type TDeleteTiktokChannelResponse = ApiResponse<TChannel>;

export const deleteTiktokChannel = async (
  payload: TDeleteTiktokChannelPayload
): Promise<TDeleteTiktokChannelResponse> => {
  const url = `/integration/tiktok/org-channel/${payload.organizationChannelId}`;

  return axiosInstance.delete(url);
};

/**
 * Use delete TikTok channel React-Query hook
 */
type TUseDeleteTiktokChannelOptions = {
  mutationConfig?: TypedMutationConfig<typeof deleteTiktokChannel, AxiosErrorCustom>;
};

export const useDeleteTiktokChannel = ({ mutationConfig }: TUseDeleteTiktokChannelOptions) => {
  const { ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: deleteTiktokChannel,
    ...restConfig,
  });
};

/**
 * Use delete TikTok channel optimistically React-Query hook
 */
type TUseDeleteTiktokChannelOptimisticallyOptions = {
  mutationConfig?: MutationConfig<typeof deleteTiktokChannel>;
};

type TDeleteTiktokChannelOptimisticallyContext = {
  previousQueries: Array<[queryKey: unknown[], data: TGetTiktokChannelsResponse]>;
};

export const useDeleteTiktokChannelOptimistically = ({
  mutationConfig,
}: TUseDeleteTiktokChannelOptimisticallyOptions) => {
  const { ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: deleteTiktokChannel,
    onMutate: async (data: TDeleteTiktokChannelValidation) => {
      await queryClient.cancelQueries({ queryKey: [GET_TIKTOK_CHANNELS_QUERY_KEY] });

      const previousQueries = queryClient.getQueriesData<TGetTiktokChannelsResponse>({
        queryKey: [GET_TIKTOK_CHANNELS_QUERY_KEY],
      });

      queryClient.setQueriesData<TGetTiktokChannelsResponse>(
        { queryKey: [GET_TIKTOK_CHANNELS_QUERY_KEY] },
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            payload: {
              ...oldData.payload,
              rows: oldData.payload.rows.filter(
                (channel) => channel.organizationChannelId !== data.organizationChannelId
              ),
            },
          };
        }
      );

      return { previousQueries } as TDeleteTiktokChannelOptimisticallyContext;
    },

    onError: (_err, _data, context) => {
      const previousQueries = (context as TDeleteTiktokChannelOptimisticallyContext)?.previousQueries;
      if (previousQueries?.length) {
        previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },

    ...restConfig,
  });
};
